<script lang="ts">
	import type { SbImage } from '../types';
	import Image from '../components/image.svelte';
	import Carousel from '$lib/carousel.svelte';
	import HeightIndicator from '$lib/techSpecs/heightIndicator.svelte';
	import WidthIndicator from '$lib/techSpecs/widthIndicator.svelte';
	import BtnAr from '$lib/buttons/v2/btnAr.svelte';
	import ViewInAr from '$lib/icons/viewInAr.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';

	interface Props {
		content: ISbStoryData<{
			title: string;
			front_view: SbImage;
			side_view: SbImage;
			height: number;
			front_view_width: number;
			side_view_width: number;
			front_view_width_indicator_length: number;
			side_view_width_indicator_length: number;
		}>['content'];
	}

	let { content }: Props = $props();

	type BinView = 'front' | 'side';

	let currentIndex = $state(0);
	let active = $derived<BinView>(currentIndex === 0 ? 'front' : 'side');
</script>

<section class="min-h-[444px]" use:editable={content}>
	{#if content.title?.length > 0}
		<h1 class="mb-10 text-[20px]/[110%] font-semibold tracking-[-.18px] text-brown-1">
			{content.title}
		</h1>
	{/if}
	<div class="lg:hidden">
		<div class="relative">
			<Carousel class="flex min-h-[40vh]" bind:currentIndex draggable>
				<div class="flex-none basis-full">
					<Image
						src={content.front_view.filename}
						class="h-full w-full select-none overflow-visible object-cover"
						alt={content.front_view.alt}
						title={content.front_view.title}
						width={1260}
					/>
				</div>

				<div class="-ml-8 flex-none basis-full">
					<Image
						src={content.side_view.filename}
						class="h-full w-full select-none overflow-visible object-cover"
						alt={content.side_view.alt}
						title={content.side_view.title}
						width={1260}
					/>
				</div>
			</Carousel>

			{#if content.height}
				<div class="stone-gradient-left absolute -left-1 top-0 h-full w-1/5">
					<div class="absolute bottom-[19%] left-1/4 h-[80%]">
						<HeightIndicator labelClass="bg-brown-6" heightLabel={content.height.toString()}
						></HeightIndicator>
					</div>
				</div>

				<div class="stone-gradient-right absolute -right-1 top-0 h-full w-1/5"></div>
			{/if}

			{#if content.front_view_width && content.side_view_width}
				<div class="absolute bottom-4 left-1/2 flex w-full -translate-x-1/2 justify-center">
					<WidthIndicator
						labelClass="bg-brown-6"
						widthLabel={active === 'front'
							? content.front_view_width.toString()
							: content.side_view_width.toString()}
						width={active === 'front' ? 33 : 25}
					/>
				</div>
			{/if}
		</div>

		<div class="mt-5 flex justify-center">
			<div class="flex gap-x-2">
				<div
					class="h-2 w-2 rounded-full"
					class:bg-stone-3={active === 'side'}
					class:bg-brown-1={active === 'front'}
				></div>
				<div
					class="h-2 w-2 rounded-full"
					class:bg-stone-3={active === 'front'}
					class:bg-brown-1={active === 'side'}
				></div>
			</div>
		</div>

		<div class="mt-6 flex justify-center lg:hidden">
			<BtnAr
				theme="transparent-brown-border"
				size={'large'}
				contentClass="flex items-center gap-x-2"
				class="mb-4"><span>View in your home</span><ViewInAr /></BtnAr
			>
		</div>
	</div>

	<div class="relative flex max-lg:hidden">
		<div class="relative -ml-[8%] flex-none basis-2/3">
			<Image
				src={content.front_view.filename}
				class="h-full w-full select-none overflow-visible object-cover"
				alt={content.front_view.alt}
				title={content.front_view.title}
				width={1260}
			/>
			<div
				class="absolute bottom-4 left-1/2 flex w-full -translate-x-1/2 justify-center 2xl:bottom-8"
			>
				<WidthIndicator
					labelClass="bg-brown-6"
					widthLabel={content.front_view_width.toString()}
					width={27}
				/>
			</div>
		</div>

		<div class="relative -ml-[20%] flex-none basis-2/3">
			<Image
				src={content.side_view.filename}
				class="h-full w-full select-none overflow-visible object-cover"
				alt={content.side_view.alt}
				title={content.side_view.title}
				width={1260}
			/>
			<div
				class="absolute bottom-4 left-1/2 flex w-full -translate-x-1/2 justify-center 2xl:bottom-8"
			>
				<WidthIndicator
					labelClass="bg-brown-6"
					widthLabel={content.side_view_width.toString()}
					width={21}
				/>
			</div>
		</div>

		<div class="absolute bottom-[19%] left-5 h-[80%]">
			<HeightIndicator labelClass="bg-brown-6" heightLabel={content.height.toString()}
			></HeightIndicator>
		</div>
	</div>
</section>

<style>
	.stone-gradient-left {
		background: linear-gradient(90deg, #faf9f9 34%, rgba(248, 244, 235, 0) 100%);
	}

	.stone-gradient-right {
		background: linear-gradient(-90deg, #faf9f9 34%, rgba(248, 244, 235, 0) 100%);
	}
</style>
